import React, { useContext, useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { GetSalesInsightsGroupedRequest, SalesInsightsCompareGroupedResponse, useSalesInsightsCompareGrouped } from '../../../../api/indicators';
import { DashboardFiltersContext } from '../filters/DashboardFiltersContext';
import { UserContext } from '../../../../context/UserContext';
import { dateRange, formatDateTimeParam, formatPeriodLabel } from '../../../../helpers/dateUtils';
import { DashboardContext } from '../../DashboardContext';
import ComparedPieBarChart, { ComparedPieBarChartData } from '../comparedCharts/ComparedPieBarChart';
import { TableChip } from '../../../../components/EnhancedTableToolbar';

const ComparedTransactionsByLocationPieBar = () =>{
    const { t } = useTranslation();
    const { locale } = useContext(UserContext);
    const { filters, dateRangeConfig } = useContext(DashboardFiltersContext);
    const { companyConfig } = useContext(DashboardContext);

    const [ params, setParams ] = useState<GetSalesInsightsGroupedRequest>({
        groupBy: 'location',
        fields: 'transactionVolume',
        startDateTime: formatDateTimeParam(filters.periodFrom || dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate),
        endDateTime: formatDateTimeParam(filters.periodTo || dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate),
        compareStartDateTime: formatDateTimeParam(filters.comparePeriodFrom || dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).startDate),
        compareEndDateTime: formatDateTimeParam(filters.comparePeriodTo || dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).endDate),
        locations: filters?.locations?.join(',') || companyConfig?.locations?.map(location => location.id).join(','),
        locationArea: filters?.locationArea?.id,
    });

    const {
        data: salesInsightsGrouped,
        isLoading,
        isError,
        refetch: fetchComparedTransactionByLocation
    } = useSalesInsightsCompareGrouped<SalesInsightsCompareGroupedResponse>(params, 'sales-insights-grouped-compare-transaction-location');
        
    function transformData(data: SalesInsightsCompareGroupedResponse | undefined): ComparedPieBarChartData[] {
        // no data
        if (!data || (!data?.salesInsightsGrouped && !data?.comparedSalesInsightsGrouped)) return [];

        // no comparison data
        if (!data.comparedSalesInsightsGrouped) {
            return data.salesInsightsGrouped.map(item => ({
                label: item.key,
                value: item.salesInsights.transactionVolume ?? 0,
                comparedValue: 0
            }));
        }

        // no current data
        if (!data.salesInsightsGrouped) {
            return data.comparedSalesInsightsGrouped.map(item => ({
                label: item.key,
                value: 0,
                comparedValue: item.salesInsights.transactionVolume ?? 0
            }));
        }

        // both periods have data
        const currentDataMap = new Map(
            data.salesInsightsGrouped.map(item => [item.key, item.salesInsights.transactionVolume])
        );
        
        const comparedDataMap = new Map(
            data.comparedSalesInsightsGrouped.map(item => [item.key, item.salesInsights.transactionVolume])
        );
        
        const allKeys = new Set([
            ...(data.salesInsightsGrouped?.map(item => item.key) || []),
            ...(data.comparedSalesInsightsGrouped?.map(item => item.key) || [])
        ]);
        
        return Array.from(allKeys).map(key => ({
            label: key,
            value: currentDataMap.get(key) ?? 0,
            comparedValue: comparedDataMap.get(key) ?? 0,
        }));
    }
            
    useEffect(() => {
        setParams(p => ({
            ...p,
            startDateTime: formatDateTimeParam(filters.periodFrom || dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate),
            endDateTime: formatDateTimeParam(filters.periodTo || dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate),
            compareStartDateTime: formatDateTimeParam(filters.comparePeriodFrom || dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).startDate),
            compareEndDateTime: formatDateTimeParam(filters.comparePeriodTo || dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).endDate),
            locations: filters?.locations?.join(',') || companyConfig?.locations?.map(location => location.id).join(','),
            locationArea: filters?.locationArea?.id,
        }))
        fetchComparedTransactionByLocation();
    }, [filters, dateRangeConfig, companyConfig, fetchComparedTransactionByLocation]);
        
        useEffect(() => {fetchComparedTransactionByLocation();}, [params, fetchComparedTransactionByLocation]);

        const currentChip: TableChip = useMemo(() => 
        ({
            label: `${formatPeriodLabel(
                new Date(filters.periodFrom ? filters.periodFrom : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate),
                locale
              )} - ${formatPeriodLabel(new Date(filters.periodTo ? filters.periodTo : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate), locale)}`,
              labelType: 'period'
        }), [filters.periodFrom, filters.periodTo]);

        const compareChip: TableChip = useMemo(() => 
        ({
            label: `${formatPeriodLabel(
                new Date(filters.comparePeriodFrom ? filters.comparePeriodFrom : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate),
                locale
              )} - ${formatPeriodLabel(new Date(filters.comparePeriodTo ? filters.comparePeriodTo : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate), locale)}`,
              labelType: 'periodCompare'
        }), [filters.comparePeriodFrom, filters.comparePeriodTo]);


    return (
        <Box>
            <ComparedPieBarChart
                title={t("screens.dashboarding.charts.transactionsByLocation")}
                description={t("screens.dashboarding.charts.volume")}
                data={transformData(salesInsightsGrouped)}
                pieLabels={[
                    `${formatPeriodLabel(new Date(filters.periodFrom ?? dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate), locale)} - ${formatPeriodLabel(new Date(filters.periodTo ?? dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate), locale)}`,
                    `${formatPeriodLabel(new Date(filters.comparePeriodFrom ?? dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).startDate), locale)} - ${formatPeriodLabel(new Date(filters.comparePeriodTo ?? dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).endDate), locale)}`
                ]}
                locale={locale}
                isLoading={isLoading}
                error={isError}
                height={560}
                reportName={t("screens.dashboarding.charts.transactionsOverview")}
                reportLink={`${process.env.REACT_APP_OAUTH_CALLBACK_URL}transactions`}
                chips={[currentChip, compareChip]}
            />            
        </Box>
    )
}

export default ComparedTransactionsByLocationPieBar;
